/**
 * Confidence-tier helpers for phantom doors (`docs/PHANTOM.md` §5): turn an
 * H1/H2 inference score into the three written tiers the volunteer sees.
 * Shared by {@link PhantomBadge} (guidance and building) and reused as-is
 * by the household feature's phantom cards, so the wording stays identical
 * everywhere a confidence score is shown.
 */
import { t } from '../../../i18n/fr';

/** The three written confidence tiers `docs/PHANTOM.md` §5 defines. */
export type PhantomConfidenceTier = 'high' | 'medium' | 'low';

/**
 * Bucket a phantom door's confidence score into one of the three written
 * tiers: forte (≥ 0.7), moyenne (≥ 0.5), faible (below).
 *
 * @param confidence - The H1/H2 inference score in [0, 1], or `null` when unknown.
 * @returns The tier, or `null` when `confidence` is `null`.
 */
export function phantomConfidenceTier(confidence: number | null): PhantomConfidenceTier | null {
  if (confidence === null) return null;
  if (confidence >= 0.7) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

/**
 * Resolve a phantom door's confidence score to its written French label.
 *
 * @param confidence - The H1/H2 inference score in [0, 1], or `null`.
 * @returns "forte", "moyenne" or "faible", or `null` when `confidence` is `null`.
 */
export function phantomConfidenceLabel(confidence: number | null): string | null {
  const tier = phantomConfidenceTier(confidence);
  if (tier === null) return null;
  return t(`session.phantom.confidence.${tier}`);
}
